import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { themeAlpine } from 'ag-grid-community';
import { Clock, ShieldAlert, ShieldCheck, AlertTriangle, Layers, BarChart3, CheckCircle2 } from 'lucide-react';
import EvidenceHeader, { evidenceHeaderControlLabelStyle, evidenceHeaderSelectStyle } from '@process-advisor/process-miner/dashboard/shared/EvidenceHeader';
import StatCard from '@process-advisor/process-miner/dashboard/shared/StatCard';
import InsightStrip from '@process-advisor/process-miner/dashboard/shared/InsightStrip';
import EvidenceFooterNote from '@process-advisor/process-miner/dashboard/shared/EvidenceFooterNote';
import { badgeCellRenderer, tierForRate } from '@process-advisor/process-miner/dashboard/shared/metricBadge';
import '@process-advisor/process-miner/shared/gridStyles.css';
import '@process-advisor/process-miner/dashboard/shared/evidenceGridStyles.css';
import {
  getSourceSystem,
  getTaskArray,
  getTaskAttributesRaw,
} from '@process-advisor/process-miner/processMiner/processMiner';
import { SOURCE_SYSTEMS, normalizeSourceSystem } from '@process-advisor/process-miner/sources/capabilities';

const C = {
  text: '#172B4D',
  body: '#42526E',
  muted: '#6B778C',
  border: '#DFE1E6',
  bg: '#F4F5F7',
  card: '#FFFFFF',
  primary: '#0747A6',
  tint: '#EAF2FF',
  green: '#00875A',
  orange: '#FF8B00',
  red: '#DE350B',
  purple: '#6554C0',
  teal: '#00A3BF',
};

const SLA_FIELD_CANDIDATES = {
  total: ['sla_total_count', 'SLA records', 'Task SLA records', 'SLA count'],
  breached: ['sla_breached_count', 'SLA breached count', 'breached SLA count'],
  hasBreach: ['sla_has_breach', 'SLA breached?', 'has_sla_breach', 'has breached'],
  paused: ['sla_paused_count', 'Paused SLA count'],
  completed: ['sla_completed_count', 'Completed SLA count'],
  cancelled: ['sla_cancelled_count', 'Cancelled SLA count'],
  elapsed: ['sla_business_elapsed_hours', 'SLA business elapsed hours', 'business elapsed hours'],
  names: ['sla_names', 'SLA names', 'SLA name', 'sla'],
};

function normKey(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function nice(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw === 'null' || raw === 'undefined') return '(blank)';
  return raw.replaceAll('^^', ', ').replace(/_/g, ' ');
}

function isSysId(value) {
  return /^[0-9a-f]{32}$/i.test(String(value || '').trim());
}

const SERVICE_NOW_ATTRIBUTE_LABELS = {
  priority: 'Priority',
  assignment_group: 'Assignment Group',
  assigned_to: 'Assigned To',
  opened_by: 'Opened By',
  closed_by: 'Closed By',
  category: 'Category',
  subcategory: 'Subcategory',
  business_service: 'Business Service',
  service_offering: 'Service Offering',
  cmdb_ci: 'Configuration Item',
  company: 'Company',
  location: 'Location',
  contact_type: 'Contact Type',
  impact: 'Impact',
  urgency: 'Urgency',
  severity: 'Severity',
};

function titleCaseAttribute(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw === 'null' || raw === 'undefined') return 'Attribute';
  const normalized = normKey(raw);
  if (SERVICE_NOW_ATTRIBUTE_LABELS[normalized]) return SERVICE_NOW_ATTRIBUTE_LABELS[normalized];

  return raw
    .replace(/[_\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => {
      const lower = word.toLowerCase();
      if (lower === 'sla') return 'SLA';
      if (lower === 'ci') return 'CI';
      if (lower === 'id') return 'ID';
      return lower ? lower.charAt(0).toUpperCase() + lower.slice(1) : '';
    })
    .join(' ');
}

function attributeDisplayLabel(attr) {
  const field = attrField(attr);
  const label = attr?.display || attr?.label || field || attr?.attributeID || attr?.id;
  const normalizedLabel = normKey(label);
  const normalizedField = normKey(field);

  if (SERVICE_NOW_ATTRIBUTE_LABELS[normalizedField]) return SERVICE_NOW_ATTRIBUTE_LABELS[normalizedField];
  if (SERVICE_NOW_ATTRIBUTE_LABELS[normalizedLabel]) return SERVICE_NOW_ATTRIBUTE_LABELS[normalizedLabel];
  return titleCaseAttribute(label || field);
}


function isSlaMetricAttribute(attr) {
  const key = normKey(attr?.attributeID || attr?.field || attr?.id || attr?.label);
  const label = normKey(attr?.label || attr?.display || attr?.field || attr?.id);
  if (!key && !label) return false;
  if (key.startsWith('sla_') || label.startsWith('sla_')) return true;
  if (/^sla($|_)/.test(key) || /^sla($|_)/.test(label)) return true;
  return /(sla_records|breached_count|has_breach|active_count|paused_count|completed_count|cancelled_count|business_elapsed|business_time_left|pause_hours|max_business_percentage|sla_names|sla_stages)/i.test(key)
    || /(sla records|sla breached|active sla|paused sla|completed sla|cancelled sla|sla business|sla pause|sla max|sla names|sla stages)/i.test(String(attr?.label || attr?.display || ''));
}

function isLifecycleOrTechnicalAttribute(attr) {
  const key = normKey(attr?.attributeID || attr?.field || attr?.id || attr?.label);
  const label = normKey(attr?.label || attr?.display || attr?.field || attr?.id);

  // In the SLA Metrics breakdown, status/lifecycle fields are not useful because
  // SLA exposure is already attached to the selected lifecycle model.  Keep the
  // breakdown to SLA Name plus true context attributes such as priority,
  // assignment_group, assigned_to, category and subcategory.
  const blocked = new Set([
    'state', 'status', 'incident_state', 'change_state', 'change_request_state',
    'workflow_state', 'lifecycle_status', 'change_lifecycle', 'task_type',
    'record_type', 'sys_class_name', 'issue_type', 'type', 'activity', 'activity_type',
  ]);

  if (blocked.has(key) || blocked.has(label)) return true;
  if (/(^|_)state$/.test(key) || /(^|_)status$/.test(key)) return true;
  if (/(^|_)type$/.test(key) && (key.includes('task') || key.includes('record') || key.includes('issue'))) return true;
  return false;
}

function attrField(attr) {
  return attr?.field || attr?.attributeID || attr?.id || attr?.label || '';
}

function attrLabel(attr) {
  return attributeDisplayLabel(attr);
}

function buildGroupOptions(attributes = []) {
  const options = [{ id: 'names', label: 'SLA Name' }];
  const seen = new Set(['names']);

  (attributes || []).forEach(attr => {
    const field = attrField(attr);
    const key = normKey(field);
    if (!key) return;
    if (isSlaMetricAttribute(attr)) return;
    if (isLifecycleOrTechnicalAttribute(attr)) return;

    const id = `attr:${key}`;
    if (seen.has(id)) return;
    seen.add(id);
    options.push({ id, label: attrLabel(attr), field });
  });

  return options;
}

function toNumber(value) {
  if (value === true) return 1;
  if (value === false || value === null || value === undefined || value === '') return 0;
  const cleaned = String(value).replace(/,/g, '').trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function toBool(value) {
  if (value === true) return true;
  const raw = String(value ?? '').trim().toLowerCase();
  return ['true', 'yes', 'y', '1', 'breached'].includes(raw);
}

function splitMulti(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw === 'null' || raw === 'undefined') return ['(blank)'];
  return raw
    .split(/\^\^|,|;|\|/g)
    .map(v => nice(v))
    .filter(Boolean)
    .slice(0, 12);
}

function formatNumber(n) {
  const value = Number(n || 0);
  if (!Number.isFinite(value)) return '0';
  return Math.round(value).toLocaleString();
}

function formatBusinessDuration(hours) {
  const value = Number(hours || 0);
  if (!Number.isFinite(value) || value <= 0) return '0h';
  const wholeHours = Math.round(value);
  const days = Math.floor(wholeHours / 24);
  const remainingHours = wholeHours % 24;
  if (days >= 1) return `${days.toLocaleString()}d ${remainingHours}h`;
  return `${wholeHours.toLocaleString()}h`;
}

function pct(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0%';
  return `${Math.round(n * 100)}%`;
}

function buildValueReader(attributes = []) {
  const byNorm = new Map();
  (attributes || []).forEach(attr => {
    [attr?.id, attr?.field, attr?.attributeID, attr?.label, attr?.display].forEach(v => {
      const key = normKey(v);
      if (key && !byNorm.has(key)) byNorm.set(key, [attr?.id, attr?.field, attr?.attributeID, attr?.label, attr?.display].filter(Boolean));
    });
  });

  return (task, fieldKey) => {
    const candidates = SLA_FIELD_CANDIDATES[fieldKey] || [fieldKey];
    const keys = [];
    candidates.forEach(candidate => {
      keys.push(candidate);
      const normalized = normKey(candidate);
      keys.push(normalized);
      if (byNorm.has(normalized)) keys.push(...byNorm.get(normalized));
    });
    for (const key of keys) {
      if (!key) continue;
      if (task?.[key] !== undefined && task?.[key] !== null && String(task[key]).trim() !== '') return task[key];
    }
    return '';
  };
}

function buildSlaMetrics(tasks = [], attributes = [], groupBy = 'names') {
  const read = buildValueReader(attributes);
  const visible = (tasks || []).filter(t => t && (t.filtered === 0 || t.filtered === undefined));
  const groups = new Map();

  const summary = {
    totalTasks: visible.length,
    tasksWithSla: 0,
    totalSlaRecords: 0,
    breachedRecords: 0,
    breachedTasks: 0,
    completedRecords: 0,
    cancelledRecords: 0,
    elapsedSum: 0,
    elapsedN: 0,
  };

  visible.forEach(task => {
    const total = toNumber(read(task, 'total'));
    const breached = toNumber(read(task, 'breached')) || (toBool(read(task, 'hasBreach')) ? 1 : 0);
    const completed = toNumber(read(task, 'completed'));
    const cancelled = toNumber(read(task, 'cancelled'));
    const elapsed = toNumber(read(task, 'elapsed'));
    const hasSla = total > 0 || breached > 0 || elapsed > 0;
    if (!hasSla) return;

    summary.tasksWithSla += 1;
    summary.totalSlaRecords += total || Math.max(1, breached || completed || cancelled || 0);
    summary.breachedRecords += breached;
    summary.breachedTasks += breached > 0 ? 1 : 0;
    summary.completedRecords += completed;
    summary.cancelledRecords += cancelled;
    if (elapsed > 0) { summary.elapsedSum += elapsed; summary.elapsedN += 1; }

    let values;
    if (groupBy === 'names') {
      values = splitMulti(read(task, 'names')).map(v => isSysId(v) ? '(unresolved SLA)' : v);
    } else if (String(groupBy).startsWith('attr:')) {
      const fieldKey = String(groupBy).slice(5);
      values = [nice(read(task, fieldKey))];
    } else {
      values = [nice(read(task, groupBy))];
    }

    values.forEach(value => {
      const key = value || '(blank)';
      if (!groups.has(key)) {
        groups.set(key, {
          group: key,
          tasks: 0,
          slaRecords: 0,
          breachedRecords: 0,
          breachedTasks: 0,
          completedRecords: 0,
          cancelledRecords: 0,
          elapsedSum: 0,
          elapsedN: 0,
        });
      }
      const row = groups.get(key);
      row.tasks += 1;
      row.slaRecords += total || Math.max(1, breached || completed || cancelled || 0);
      row.breachedRecords += breached;
      row.breachedTasks += breached > 0 ? 1 : 0;
      row.completedRecords += completed;
      row.cancelledRecords += cancelled;
      if (elapsed > 0) { row.elapsedSum += elapsed; row.elapsedN += 1; }
    });
  });

  const rows = [...groups.values()].map(row => ({
    ...row,
    breachRate: row.slaRecords ? row.breachedRecords / row.slaRecords : 0,
    taskBreachRate: row.tasks ? row.breachedTasks / row.tasks : 0,
    avgBusinessElapsedHours: row.elapsedN ? row.elapsedSum / row.elapsedN : 0,
  })).sort((a, b) => (b.breachedRecords - a.breachedRecords) || (b.slaRecords - a.slaRecords) || (b.tasks - a.tasks));

  return {
    summary: {
      ...summary,
      taskSlaCoverage: summary.totalTasks ? summary.tasksWithSla / summary.totalTasks : 0,
      breachRate: summary.totalSlaRecords ? summary.breachedRecords / summary.totalSlaRecords : 0,
      breachedTaskRate: summary.tasksWithSla ? summary.breachedTasks / summary.tasksWithSla : 0,
      avgElapsedHours: summary.elapsedN ? summary.elapsedSum / summary.elapsedN : 0,
    },
    rows,
  };
}

export default function SlaMetrics({ refreshKey, processLabel = 'Process', entityLabel = 'Work Item' }) {
  const [groupBy, setGroupBy] = useState('names');
  const [versionTick, setVersionTick] = useState(0);

  useEffect(() => setVersionTick(v => v + 1), [refreshKey]);

  const sourceSystem = normalizeSourceSystem(getSourceSystem ? getSourceSystem() : 'generic');
  const isServiceNow = sourceSystem === SOURCE_SYSTEMS.SERVICENOW;
  // Use two attribute sets:
  // 1. allAttributes includes derived SLA fields and is required to calculate the SLA metrics.
  // 2. breakdownAttributes excludes derived SLA fields and is used only for the dropdown.
  // The previous cleanup accidentally used the filtered list for both, so SLA records disappeared.
  const allAttributes = getTaskAttributesRaw ? getTaskAttributesRaw({ includeDerivedSla: true }) : [];
  const breakdownAttributes = getTaskAttributesRaw ? getTaskAttributesRaw() : [];
  const groupOptions = useMemo(() => buildGroupOptions(breakdownAttributes), [breakdownAttributes, versionTick]);

  useEffect(() => {
    if (!groupOptions.some(o => o.id === groupBy)) setGroupBy('names');
  }, [groupOptions, groupBy]);

  const data = useMemo(() => buildSlaMetrics(
    getTaskArray ? getTaskArray() : [],
    allAttributes,
    groupBy
  ), [groupBy, versionTick, allAttributes]);

  const summary = data.summary;
  const rows = data.rows;
  const groupLabel = groupOptions.find(o => o.id === groupBy)?.label || 'SLA Name';

  const columnDefs = useMemo(() => [
    { field: 'group', headerName: groupLabel, minWidth: 220, flex: 1.5, pinned: 'left', filter: 'agTextColumnFilter', cellStyle: { fontWeight: 700, color: C.text }, tooltipField: 'group' },
    { field: 'tasks', headerName: 'Tasks', minWidth: 90, flex: 0.7, type: 'numericColumn', headerClass: 'num-header', cellClass: 'num-cell', filter: 'agNumberColumnFilter', valueFormatter: p => formatNumber(p.value) },
    { field: 'slaRecords', headerName: 'SLA Records', minWidth: 110, flex: 0.8, type: 'numericColumn', headerClass: 'num-header', cellClass: 'num-cell', filter: 'agNumberColumnFilter', valueFormatter: p => formatNumber(p.value) },
    { field: 'breachedRecords', headerName: 'Breached', minWidth: 100, flex: 0.75, type: 'numericColumn', headerClass: 'num-header', cellClass: 'num-cell', filter: 'agNumberColumnFilter', valueFormatter: p => formatNumber(p.value) },
    {
      field: 'breachRate',
      headerName: 'Breach %',
      minWidth: 100,
      flex: 0.75,
      type: 'numericColumn',
      headerClass: 'num-header',
      cellClass: 'num-cell',
      filter: 'agNumberColumnFilter',
      valueFormatter: p => pct(p.value),
      cellRenderer: badgeCellRenderer(v => pct(v), v => tierForRate(v, { watch: 0.1, high: 0.2 })),
    },
    {
      field: 'taskBreachRate',
      headerName: 'Task Breach %',
      minWidth: 120,
      flex: 0.85,
      type: 'numericColumn',
      headerClass: 'num-header',
      cellClass: 'num-cell',
      filter: 'agNumberColumnFilter',
      valueFormatter: p => pct(p.value),
      cellRenderer: badgeCellRenderer(v => pct(v), v => tierForRate(v, { watch: 0.1, high: 0.2 })),
    },
    { field: 'avgBusinessElapsedHours', headerName: 'Avg Business Elapsed', minWidth: 150, flex: 1.0, type: 'numericColumn', headerClass: 'num-header', cellClass: 'num-cell', filter: 'agNumberColumnFilter', valueFormatter: p => formatBusinessDuration(p.value), cellStyle: { fontWeight: 600 } },
  ], [groupLabel]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
  }), []);

  if (!isServiceNow) {
    return <div style={{ padding: 16, color: C.muted }}>SLA Metrics are available for ServiceNow models that include task_sla data.</div>;
  }

  const topBreach = rows[0] || null;
  const topElapsed = [...rows].sort((a, b) => b.avgBusinessElapsedHours - a.avgBusinessElapsedHours)[0] || null;

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: C.text }}>
      <EvidenceHeader
        icon={<ShieldCheck size={18} />}
        title="SLA Metrics"
        subtitle="Track ServiceNow SLA exposure, breaches, completed SLA records, and elapsed business time."
        actions={(
          <>
            <label style={evidenceHeaderControlLabelStyle}>Breakdown</label>
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)} style={{ ...evidenceHeaderSelectStyle, minWidth: 200 }}>
              {groupOptions.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </>
        )}
      />

      {summary.tasksWithSla <= 0 ? (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, color: C.body, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} color={C.orange} />
          <div>
            <div style={{ fontWeight: 800, marginBottom: 4 }}>No task_sla metrics found in this model.</div>
            <div style={{ fontSize: 13, color: C.muted }}>Run extraction with ServiceNow task_sla enabled. This section will populate when SLA fields such as SLA records, breached count, completed counts, business elapsed hours, and SLA names are present on the work items.</div>
          </div>
        </div>
      ) : (
        <>
          <div className="evidence-metric-grid">
            <StatCard icon={Layers} label="Tasks with SLA" value={formatNumber(summary.tasksWithSla)} detail={`${pct(summary.taskSlaCoverage)} coverage`} color={C.primary} />
            <StatCard icon={ShieldAlert} label="SLA Breach Rate" value={pct(summary.breachRate)} detail={`${formatNumber(summary.breachedRecords)} breached records`} color={summary.breachRate > 0.2 ? C.red : C.orange} />
            <StatCard icon={AlertTriangle} label="Breached Tasks" value={formatNumber(summary.breachedTasks)} detail={`${pct(summary.breachedTaskRate)} of SLA tasks`} color={C.orange} />
            <StatCard icon={Clock} label="Avg Business Elapsed" value={formatBusinessDuration(summary.avgElapsedHours)} detail="Per SLA-enabled task" color={C.teal} />
            <StatCard icon={CheckCircle2} label="Completed SLA Records" value={formatNumber(summary.completedRecords)} detail={`${formatNumber(summary.totalSlaRecords)} total SLA records`} color={C.green} />
          </div>

          <InsightStrip
            style={{ marginBottom: 10 }}
            items={[
              { label: 'Highest breach exposure', value: topBreach ? `${topBreach.group} (${pct(topBreach.breachRate)})` : 'Not available' },
              { label: 'Longest business elapsed', value: topElapsed?.avgBusinessElapsedHours ? `${topElapsed.group} (${formatBusinessDuration(topElapsed.avgBusinessElapsedHours)})` : 'Not available' },
            ]}
          />

          <div className="evidence-grid-shell evidence-ag-grid" style={{ width: '100%' }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              animateRows={false}
              suppressCellFocus={true}
              domLayout="autoHeight"
              rowHeight={40}
              headerHeight={48}
              enableCellTextSelection={true}
            />
          </div>

          <EvidenceFooterNote icon={<BarChart3 size={14} />}>
            SLA Metrics are derived from task_sla aggregates on each {entityLabel.toLowerCase()}. Use SLA Name and selected model attributes to identify breach drivers.
          </EvidenceFooterNote>
        </>
      )}
    </div>
  );
}
